import React, { useState, useEffect, useRef } from "react";
import './Pages.css';
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import dele from "./icon/deletepage.jpg";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";

// ── Performance helpers ───────────────────────────────────────────────────────
const SLOW_MS      = 2000;
const CACHE_KEY    = "pages_list_cache";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — matches Shop.js

function checkSlow(label, t0) {
    const ms = performance.now() - t0;
    if (ms > SLOW_MS)
        console.error(`[SLOW] ${label} took ${(ms / 1000).toFixed(2)}s — exceeds ${SLOW_MS / 1000}s threshold`);
}
// Use localStorage (not sessionStorage) with a TTL so pages render instantly
// even after a tab close/reload — same pattern as Shop.js shop_cache.
function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - (parsed.cachedAt || 0) > CACHE_TTL_MS) {
            localStorage.removeItem(CACHE_KEY);
            return null;
        }
        return parsed;
    } catch { return null; }
}
function writeCache(data) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, cachedAt: Date.now() })); } catch {}
}
function clearCache() { try { localStorage.removeItem(CACHE_KEY); } catch {} }

// ── Store prefetch — fires before React navigates so Store.js finds data ─────
// Mirrors the same endpoints and cache keys Store.js uses (_eagerPrefetch).
// We write to localStorage with the {val, exp} TTL wrapper Posts.js/about.js
// expect, AND to the store_header key Store.js reads on mount.
const _STORE_API = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';
const _STORE_CACHE_TTL = 5 * 60 * 1000;
const _inflight = new Map();

function _storeLSSet(key, val) {
    try { localStorage.setItem(key, JSON.stringify({ val, exp: Date.now() + _STORE_CACHE_TTL })); } catch {}
}

function prefetchStore(pageid) {
    if (!pageid || _inflight.has(pageid)) return;
    // header — gives Store.js instant title/images, written as store_header:{id}
    const headerP = fetch(`${_STORE_API}/api/get_store_header?pageid=${pageid}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => {
            if (!d) return;
            const entry = {
                pagename:          d.pagename          || "",
                description:       d.description       || "",
                profile_image_url: d.profile_image_url || "",
                banner_image_url:  d.banner_image_url  || "",
            };
            try { localStorage.setItem(`store_header:${pageid}`, JSON.stringify(entry)); } catch {}
        }).catch(() => {});
    // posts — written as { val, exp } so Posts.js storageGet finds it
    const postsP = fetch(`${_STORE_API}/page/${pageid}/posts`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) _storeLSSet(`page_posts:${pageid}`, d); })
        .catch(() => {});
    // about — written as { val, exp } so about.js storageGet finds it
    const aboutP = fetch(`${_STORE_API}/get_about_page_for_store?pageid=${pageid}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) _storeLSSet(`about:${pageid}`, d); })
        .catch(() => {});
    _inflight.set(pageid, Promise.all([headerP, postsP, aboutP])
        .finally(() => _inflight.delete(pageid)));
}

// State and city data for USA and India
const countryData = {
  US: {
    name: "United States",
    states: {
      CA: {
        name: "California",
        cities: ["San Jose"]
      },
    }
  },
  IN: {
    name: "India",
    states: {
      KA: {
        name: "Karnataka",
        cities: ["Udupi", "Kundapura", "Karkala", "Byndoor", "Brahmavar", "Hebri", "Kaup"]
      }
    }
  }
};

function Pages() {
    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';
    const [pageType, setPageType] = useState(""); // "Firm" or "Individual"
    const [showForm, setShowForm] = useState(false);
    const [showDetailsForm, setShowDetailsForm] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pageToDelete, setPageToDelete] = useState(null);
    const navigate = useNavigate();
    const tokenRef = useRef(localStorage.getItem("token"));

    // Seed from cache so cards render instantly on revisit
    const _cached = readCache();
    const [pages, setPages] = useState(_cached || {
        mypages: [],
        otherpages: [],
        has_more_mypages: false,
        all_mypages: [],
        has_more_otherpages: false,
        all_otherpages: []
    });

    const [loading, setLoading] = useState(!_cached);
    const [error, setError] = useState(null);
    const [showTypeSelection, setShowTypeSelection] = useState(false);

    // Step 1: Basic info
    const [newPage, setNewPage] = useState({
        name: "",
        description: "",
    });

    // Step 2: Details
    const [profileImage, setProfileImage] = useState(null);
    const [bannerImage, setBannerImage] = useState(null);
    const [aboutInfo, setAboutInfo] = useState({
        paragraphs: [""],
        website: "",
        industry: "",
        company: "",
        headquarter: "",
        founded: "",
        specialties: "",
        address: "",
        country: "",
        state: "",
        city: "",
        zipCode: ""
    });

    const [isCreating, setIsCreating] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        name: false,
        description: false,
        address: false,
        country: false,
        state: false,
        city: false,
        zipCode: false
    });
    
    // Address dropdown states
    const [countries] = useState([
        { code: "US", name: "United States" },
        { code: "IN", name: "India" }
    ]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [showMoreMyPages, setShowMoreMyPages] = useState(false);
    const [showMoreOtherPages, setShowMoreOtherPages] = useState(false);
    // profile_image_url is now returned inline by List_page_api — no separate
    // per-page image requests needed. pageProfileImages kept for compatibility.
    // Restore image map from cache so navState has the real profile_image_url
    // immediately — Store.js uses it for zero-network first paint.
    const [pageProfileImages, setPageProfileImages] = useState(() => {
        if (!_cached) return {};
        const map = {};
        [...(_cached.mypages || []), ...(_cached.all_otherpages || []), ...(_cached.all_mypages || [])]
            .forEach(p => { if (p.id && p.profile_image_url) map[p.id] = p.profile_image_url; });
        return map;
    });

    // Update states when country changes
    useEffect(() => {
        if (aboutInfo.country && countryData[aboutInfo.country]) {
            const countryStates = Object.entries(countryData[aboutInfo.country].states).map(
                ([code, state]) => ({ code, name: state.name })
            );
            setStates(countryStates);
        } else {
            setStates([]);
        }
        // Reset state and city when country changes
        setAboutInfo(prev => ({
            ...prev,
            state: "",
            city: ""
        }));
    }, [aboutInfo.country]);

    // Update cities when state changes
    useEffect(() => {
        if (aboutInfo.country && aboutInfo.state && countryData[aboutInfo.country]?.states[aboutInfo.state]) {
            setCities(countryData[aboutInfo.country].states[aboutInfo.state].cities);
        } else {
            setCities([]);
        }
        // Reset city when state changes
        setAboutInfo(prev => ({
            ...prev,
            city: ""
        }));
    }, [aboutInfo.state]);

    // Validate form fields
    const validateForm = () => {
        const errors = {
            name: !newPage.name.trim(),
            description: !newPage.description.trim(),
            address: !aboutInfo.address.trim(),
            country: !aboutInfo.country.trim(),
            state: !aboutInfo.state.trim(),
            city: !aboutInfo.city.trim(),
            zipCode: !aboutInfo.zipCode.trim()
        };
        
        setValidationErrors(errors);
        return !Object.values(errors).some(error => error);
    };

    // Fetch Pages (My Pages and Other Pages)
    useEffect(() => {
        const fetchPages = async () => {
            const token = tokenRef.current;
            if (!token) {
                alert("User not logged in");
                return;
            }

            const t0 = performance.now();
            try {
                const response = await fetch(`${API_URL}/page/List_page_api`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                });

                checkSlow("GET /page/List_page_api", t0);

                if (!response.ok) {
                    throw new Error("Failed to fetch pages");
                }

                const data = await response.json();

                // profile_image_url is now embedded per page by the backend (no N+1 requests).
                // Populate pageProfileImages from the response so existing JSX works unchanged.
                const imageMap = {};
                [...(data.mypages || []), ...(data.otherpages || []), ...(data.all_mypages || [])]
                    .forEach(p => { if (p.id) imageMap[p.id] = p.profile_image_url || null; });
                setPageProfileImages(imageMap);

                const normalized = {
                    mypages: data.mypages,
                    otherpages: data.otherpages.slice(0, 4),
                    has_more_mypages: data.has_more_mypages,
                    all_mypages: data.all_mypages,
                    all_otherpages: data.otherpages,
                    has_more_otherpages: data.otherpages.length > 4
                };
                writeCache(normalized);
                setPages(normalized);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchPages();
    }, [API_URL]);

    // Step 1 handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPage({ ...newPage, [name]: value });
        // Clear validation error when user types
        if (name === "name" || name === "description") {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    // Step 2 handlers
    const handleProfileImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            setValidationErrors(prev => ({ ...prev, profileImage: false }));
        }
    };
    
    const handleBannerImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBannerImage(file);
            setValidationErrors(prev => ({ ...prev, bannerImage: false }));
        }
    };
    
    const handleAboutInfoChange = (field, value) => {
        setAboutInfo(prev => ({ ...prev, [field]: value }));
        // Clear validation error when user selects/inputs
        if (["address", "country", "state", "city", "zipCode"].includes(field)) {
            setValidationErrors(prev => ({ ...prev, [field]: false }));
        }
    };
    
    const handleParagraphChange = (index, value) => {
        const updated = [...aboutInfo.paragraphs];
        updated[index] = value;
        setAboutInfo(prev => ({ ...prev, paragraphs: updated }));
    };
    
    const handleAddParagraph = () => {
        setAboutInfo(prev => ({ ...prev, paragraphs: [...prev.paragraphs, ""] }));
    };
    
    const handleDeleteParagraph = (index) => {
        setAboutInfo(prev => ({
            ...prev,
            paragraphs: prev.paragraphs.filter((_, i) => i !== index)
        }));
    };

    // Step 1: Next
    const handleNext = () => {
    const hasErrors = !newPage.name.trim() || !newPage.description.trim();

    setValidationErrors(prev => ({
        ...prev,
        name: !newPage.name.trim(),
        description: !newPage.description.trim()
    }));

    if (hasErrors) return;

    // Show firm/individual selection before proceeding
    setShowForm(false);
    setShowTypeSelection(true);
};


    // Step 2: Save All
    const handleSaveAll = async () => {
        if (!validateForm()) {
            return;
        }
        
        setIsCreating(true);
        const token = localStorage.getItem("token");
        const t0 = performance.now();
        try {
            // 1. Create the page
            const pageRes = await fetch(`${API_URL}/page/create_new_page_api`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    pagename: newPage.name,
                    description: newPage.description,
                    page_type: pageType
                }),
            });

            if (!pageRes.ok) throw new Error("Failed to create page");
            const pageData = await pageRes.json();
            const newPageId = pageData.pageID;

            // 2 & 3: Upload profile + banner images in parallel
            // FIX: Both endpoints are POST (not PUT). 'PuT' was a typo that broke uploads.
            const uploadPromises = [];
            if (profileImage) {
                const formData = new FormData();
                formData.append('page_id', newPageId);
                formData.append('profile_image', profileImage);
                uploadPromises.push(fetch(`${API_URL}/api/update_profile_image`, {
                    method: 'POST',  // FIX: was 'PuT' (wrong case + wrong verb)
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                }));
            }
            if (bannerImage) {
                const formData = new FormData();
                formData.append('page_id', newPageId);
                formData.append('banner_image', bannerImage);
                uploadPromises.push(fetch(`${API_URL}/api/update_banner_image`, {
                    method: 'POST',  // FIX: was 'PUT' but Flask route expects POST
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                }));
            }
            // 4. Save about info — run in parallel with image uploads
            uploadPromises.push(fetch(`${API_URL}/save_about_page`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                // AFTER:
body: JSON.stringify({
    pageid: newPageId,
    description: newPage.description,   // ← ADD THIS
    ...aboutInfo
}),
            }));

            await Promise.all(uploadPromises);

            checkSlow("handleSaveAll (full page creation)", t0);

            // 5. Redirect
            clearCache();
            setShowDetailsForm(false);
            setIsCreating(false);
            setNewPage({ name: "", description: "" });
            setProfileImage(null);
            setBannerImage(null);
            setAboutInfo({
                paragraphs: [""],
                website: "",
                industry: "",
                company: "",
                headquarter: "",
                founded: "",
                specialties: "",
                address: "",
                country: "",
                state: "",
                city: "",
                zipCode: ""
            });
            localStorage.setItem('pageId', newPageId);
            localStorage.setItem('pagename', newPage.name);
            localStorage.setItem('description', newPage.description);
            navigate("/EditStore");
        } catch (err) {
            alert("Failed to create page. Please try again.");
            setIsCreating(false);
        }
    };

    // Delete Page
    const showDeleteConfirmation = (pageId) => {
        setPageToDelete(pageId);
        setShowDeleteModal(true);
    };

    const deletePage = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            alert("User not logged in");
            return;
        }

        const t0 = performance.now();
        try {
            const response = await fetch(
                `${API_URL}/page/delete_page_api?pageid=${pageToDelete}`,
                {
                    method: "DELETE",
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                }
            );

            checkSlow(`DELETE /page/delete_page_api pageid=${pageToDelete}`, t0);

            if (!response.ok) {
                const errorData = await response.json();
                alert(`Error: ${errorData.error}`);
                return;
            }

            // Remove the deleted page from local state
            const updatedPages = pages.mypages.filter((page) => page.id !== pageToDelete);
            const updatedAllPages = pages.all_mypages.filter((page) => page.id !== pageToDelete);
            clearCache();
            setPages({
                ...pages,
                mypages: updatedPages,
                all_mypages: updatedAllPages,
                has_more_mypages: updatedAllPages.length > 4
            });

            setShowDeleteModal(false);
            setPageToDelete(null);
        } catch (err) {
            alert("Failed to delete page. Please try again.");
        }
    };

    // Cancel Delete
    const cancelDelete = () => {
        setShowDeleteModal(false);
        setPageToDelete(null);
    };

    const toggleShowMoreMyPages = () => {
        setShowMoreMyPages(!showMoreMyPages);
    };

    const toggleShowMoreOtherPages = () => {
        setShowMoreOtherPages(!showMoreOtherPages);
    };

    if (error) return <div>Error: {error}</div>;

    const myPagesToDisplay = showMoreMyPages ? pages.all_mypages : pages.mypages;
    const otherPagesToDisplay = showMoreOtherPages ? pages.all_otherpages : pages.otherpages;

    return (
        <>
            <Header />
            <div className="pages-div-out">
                <div className="pages-create-display">
                    <button
                        className="btn-create-page"
                        onClick={() => setShowForm(true)}
                        disabled={isCreating}
                    >
                        {isCreating ? "Creating..." : "+ Create a Page"}
                    </button>
                    {showTypeSelection && (
    <div className="form-modal-page">
        <div className="form-content-page" style={{ textAlign: 'center' }}>
            <h3 className="h3-page">Who is this page for?</h3>
            <div style={{ 
                display: 'flex', 
                gap: '20px', 
                margin: '20px 0',
                justifyContent: 'center'
            }}>
                <button 
                    className="btn-select-type firm-btn" 
                    onClick={() => {
                        setPageType("Firm");
                        setShowTypeSelection(false);
                        setShowDetailsForm(true);
                    }}
                >
                    <div className="btn-icon">🏢</div>
                    <div className="btn-text">Firm</div>
                    <div className="btn-description">For businesses & organizations</div>
                </button>
                <button 
                    className="btn-select-type individual-btn" 
                    onClick={() => {
                        setPageType("Individual");
                        setShowTypeSelection(false);
                        setShowDetailsForm(true);
                    }}
                >
                    <div className="btn-icon">👤</div>
                    <div className="btn-text">Individual</div>
                    <div className="btn-description">For personal brands</div>
                </button>
            </div>
            <button 
                className="btn-back-outline" 
                onClick={() => {
                    setShowTypeSelection(false);
                    setShowForm(true);
                }}
            >
                ← Back
            </button>
        </div>
    </div>
)}
                    {/* Step 1: Basic Info Modal */}
                    {showForm && (
                        <div className="form-modal-page">
                            <div className="form-content-page">
                                <h3 className="h3-page">Create New Page</h3>
                                <div className="form-scroll-container">
                                    <input
                                        className={`text-page ${validationErrors.name ? "error-field" : ""}`}
                                        type="text"
                                        name="name"
                                        placeholder="Enter Name of Page you want to create"
                                        value={newPage.name}
                                        onChange={handleInputChange}
                                    />
                                    {validationErrors.name && (
                                        <p className="error-message">Page name is required</p>
                                    )}
                                    <input
                                        className={`text-page ${validationErrors.description ? "error-field" : ""}`}
                                        type="text"
                                        name="description"
                                        placeholder="Enter Description (e.g. what products/services you offer)"
                                        value={newPage.description}
                                        onChange={handleInputChange}
                                    />
                                    {validationErrors.description && (
                                        <p className="error-message">Description is required</p>
                                    )}
                                </div>
                                <div className="form-actions-page">
                                    <button onClick={handleNext} disabled={isCreating}>
                                        Next
                                    </button>
                                    <button onClick={() => setShowForm(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Step 2: Details Modal */}
                    {showDetailsForm && (
  <div className="flipkart-modal-overlay">
    <div className="flipkart-modal-container">
      <div className="flipkart-modal-header">
        <h3>Page Details</h3>
        <button
          className="flipkart-modal-close"
          onClick={() => setShowDetailsForm(false)}
        >
          &times;
        </button>
      </div>
      <div className="flipkart-modal-body scrollable-content">
        <div className="flipkart-form-group">
          <label>Profile Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleProfileImageUpload}
            className="flipkart-file-input"
          />
        </div>

        <div className="flipkart-form-group">
          <label>Banner Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleBannerImageUpload}
            className="flipkart-file-input"
          />
        </div>

        <div className="flipkart-form-group">
          <label>Add Information about your Page</label>
          {aboutInfo.paragraphs.map((para, idx) => (
            <div key={idx} className="paragraph-group">
              <textarea
                value={para}
                onChange={(e) => handleParagraphChange(idx, e.target.value)}
                className="flipkart-textarea"
                placeholder={`Paragraph ${idx + 1}`}
              />
              <button
                className="flipkart-button-danger small"
                onClick={() => handleDeleteParagraph(idx)}
              >
                Delete
              </button>
            </div>
          ))}
          <button
            className="flipkart-button-secondary small"
            onClick={handleAddParagraph}
          >
            + Add Paragraph
          </button>
        </div>

        {pageType === "Firm" && (
          <>
            <div className="flipkart-form-group">
              <label>Website</label>
              <input
                type="text"
                placeholder="Website"
                value={aboutInfo.website}
                onChange={(e) => handleAboutInfoChange("website", e.target.value)}
                className="flipkart-input"
              />
            </div>

            <div className="flipkart-form-group">
              <label>Industry</label>
              <input
                type="text"
                placeholder="Industry"
                value={aboutInfo.industry}
                onChange={(e) => handleAboutInfoChange("industry", e.target.value)}
                className="flipkart-input"
              />
            </div>

            <div className="flipkart-form-group">
              <label>Company Size</label>
              <input
                type="text"
                placeholder="Company Size"
                value={aboutInfo.company}
                onChange={(e) => handleAboutInfoChange("company", e.target.value)}
                className="flipkart-input"
              />
            </div>

            <div className="flipkart-form-group">
              <label>Founded</label>
              <input
                type="month"
                placeholder="Founded"
                value={aboutInfo.founded}
                onChange={(e) => handleAboutInfoChange("founded", e.target.value)}
                className="flipkart-input"
              />
            </div>

            <div className="flipkart-form-group">
              <label>Headquarters</label>
              <input
                type="text"
                placeholder="Headquarters"
                value={aboutInfo.headquarter}
                onChange={(e) => handleAboutInfoChange("headquarter", e.target.value)}
                className="flipkart-input"
              />
            </div>

            <div className="flipkart-form-group">
              <label>Specialties</label>
              <input
                type="text"
                placeholder="Specialties"
                value={aboutInfo.specialties}
                onChange={(e) => handleAboutInfoChange("specialties", e.target.value)}
                className="flipkart-input"
              />
            </div>
          </>
        )}

        <div className="flipkart-form-group">
          <label>
            Address <span className="required-star">*</span>
          </label>
          <input
            type="text"
            placeholder="Address"
            value={aboutInfo.address}
            onChange={(e) => handleAboutInfoChange("address", e.target.value)}
            className={`flipkart-input ${
              validationErrors.address ? "error-field" : ""
            }`}
          />
          {validationErrors.address && (
            <p className="error-message">Address is required</p>
          )}
        </div>

        <div className="flipkart-form-group">
          <label>
            Country <span className="required-star">*</span>
          </label>
          <select
            value={aboutInfo.country}
            onChange={(e) => handleAboutInfoChange("country", e.target.value)}
            className={`flipkart-input ${
              validationErrors.country ? "error-field" : ""
            }`}
          >
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          {validationErrors.country && (
            <p className="error-message">Country is required</p>
          )}
        </div>

        <div className="flipkart-form-group">
          <label>
            State/Province <span className="required-star">*</span>
          </label>
          <select
            value={aboutInfo.state}
            onChange={(e) => handleAboutInfoChange("state", e.target.value)}
            className={`flipkart-input ${
              validationErrors.state ? "error-field" : ""
            }`}
            disabled={!aboutInfo.country}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
          {validationErrors.state && (
            <p className="error-message">State is required</p>
          )}
        </div>

        <div className="flipkart-form-group">
          <label>
            City <span className="required-star">*</span>
          </label>
          <select
            value={aboutInfo.city}
            onChange={(e) => handleAboutInfoChange("city", e.target.value)}
            className={`flipkart-input ${
              validationErrors.city ? "error-field" : ""
            }`}
            disabled={!aboutInfo.state}
          >
            <option value="">Select City</option>
            {cities.map((city, index) => (
              <option key={index} value={city}>
                {city}
              </option>
            ))}
          </select>
          {validationErrors.city && (
            <p className="error-message">City is required</p>
          )}
        </div>

        <div className="flipkart-form-group">
          <label>
            Zip Code <span className="required-star">*</span>
          </label>
          <input
            type="text"
            placeholder="Zip Code"
            value={aboutInfo.zipCode}
            onChange={(e) => handleAboutInfoChange("zipCode", e.target.value)}
            className={`flipkart-input ${
              validationErrors.zipCode ? "error-field" : ""
            }`}
          />
          {validationErrors.zipCode && (
            <p className="error-message">Zip code is required</p>
          )}
        </div>
      </div>

      <div className="flipkart-modal-footer">
        <button
          className="flipkart-button-secondary"
          onClick={() => {
            setShowDetailsForm(false);
            setShowTypeSelection(true);
          }}
        >
          Back
        </button>
        <button
          className="flipkart-button-primary"
          onClick={handleSaveAll}
          disabled={isCreating}
        >
          {isCreating ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  </div>
)}
                    <div className="pages-two-columns">
                    <div className="pages-list-pro">
                        <h3 className="pages-section-title">My Pages</h3>
                        {pages.mypages.length === 0 && loading ? (
                            <div className="loading-state">Loading...</div>
                        ) : myPagesToDisplay.length === 0 ? (
                            <div className="empty-state">No pages yet. Create your first page!</div>
                        ) : (
                            <div className="pages-grid">
                                {myPagesToDisplay.map((page) => (
                                    <div key={page.id} className="page-card-modern">
                                        <div 
                                            className="page-card-image-container"
                                            onClick={() => {
                                                localStorage.setItem('pageId', page.id);
                                                localStorage.setItem('pagename', page.pagename);
                                                localStorage.setItem('description', page.description);
                                                navigate("/EditStore");
                                            }}
                                        >
                                            {pageProfileImages[page.id] ? (
                                                <img 
                                                    src={pageProfileImages[page.id]} 
                                                    alt={page.pagename}
                                                    className="page-card-image"
                                                />
                                            ) : (
                                                <div className="page-card-placeholder">
                                                    <span className="page-card-placeholder-icon">📄</span>
                                                </div>
                                            )}
                                            <div className="page-card-overlay">
                                                <span className="page-card-view-text">View Page</span>
                                            </div>
                                        </div>
                                        <div className="page-card-content">
                                            <h4 
                                                className="page-card-title"
                                                onClick={() => {
                                                    localStorage.setItem('pageId', page.id);
                                                    localStorage.setItem('pagename', page.pagename);
                                                    localStorage.setItem('description', page.description);
                                                    navigate("/EditStore");
                                                }}
                                            >
                                                {page.pagename}
                                            </h4>
                                            {page.description && (
                                                <p className="page-card-description">
                                                    {page.description.length > 80 
                                                        ? `${page.description.substring(0, 80)}...` 
                                                        : page.description}
                                                </p>
                                            )}
                                            <div className="page-card-actions">
                                                <button
                                                    className="page-card-edit-btn"
                                                    onClick={() => {
                                                        localStorage.setItem('pageId', page.id);
                                                        localStorage.setItem('pagename', page.pagename);
                                                        localStorage.setItem('description', page.description);
                                                        navigate("/EditStore");
                                                    }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="page-card-delete-btn"
                                                    onClick={() => showDeleteConfirmation(page.id)}
                                                >
                                                    <img src={dele} width={"16px"} height={"16px"} alt="Delete" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {pages.has_more_mypages && (
                            <button className="more-button-modern" onClick={toggleShowMoreMyPages}>
                                {showMoreMyPages ? (
                                    <>Show Less <FaChevronUp /></>
                                ) : (
                                    <>Show More <FaChevronDown /></>
                                )}
                            </button>
                        )}
                    </div>

                    <div className="pages-list-pro">
                        <h3 className="pages-section-title">Other Pages</h3>
                        {loading ? (
                            <div className="loading-state">Loading...</div>
                        ) : otherPagesToDisplay.length === 0 ? (
                            <div className="empty-state">No other pages to display</div>
                        ) : (
                            <div className="pages-grid">
                                {otherPagesToDisplay.map((otherpage) => {
                                    // Build nav state once per card — Store.js reads this
                                    // for zero-lag first paint (title, description, image).
                                    const navState = {
                                        title:             otherpage.pagename,
                                        description:       otherpage.description || "",
                                        profile_image_url: pageProfileImages[otherpage.id] || "",
                                    };
                                    const goToStore = () => navigate(
                                        `/store/${otherpage.id}`,
                                        { state: navState }
                                    );
                                    return (
                                    <div
                                        key={otherpage.id}
                                        className="page-card-modern page-card-other"
                                        // Prefetch all 3 Store endpoints on hover — fires ~200ms
                                        // before click so data is ready by the time Store mounts.
                                        onMouseEnter={() => prefetchStore(otherpage.id)}
                                        // Also prefetch on touch-start for mobile
                                        onTouchStart={() => prefetchStore(otherpage.id)}
                                    >
                                        <div
                                            className="page-card-image-container"
                                            onClick={goToStore}
                                        >
                                            {pageProfileImages[otherpage.id] ? (
                                                <img 
                                                    src={pageProfileImages[otherpage.id]} 
                                                    alt={otherpage.pagename}
                                                    className="page-card-image"
                                                />
                                            ) : (
                                                <div className="page-card-placeholder">
                                                    <span className="page-card-placeholder-icon">📄</span>
                                                </div>
                                            )}
                                            <div className="page-card-overlay">
                                                <span className="page-card-view-text">View Page</span>
                                            </div>
                                        </div>
                                        <div className="page-card-content">
                                            <h4
                                                className="page-card-title"
                                                onClick={goToStore}
                                            >
                                                {otherpage.pagename}
                                            </h4>
                                            {otherpage.description && (
                                                <p className="page-card-description">
                                                    {otherpage.description.length > 80 
                                                        ? `${otherpage.description.substring(0, 80)}...` 
                                                        : otherpage.description}
                                                </p>
                                            )}
                                            <div className="page-card-actions">
                                                <button
                                                    className="page-card-view-btn"
                                                    onClick={goToStore}
                                                >
                                                    Visit Page
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                        {pages.has_more_otherpages && (
                            <button className="more-button-modern" onClick={toggleShowMoreOtherPages}>
                                {showMoreOtherPages ? (
                                    <>Show Less <FaChevronUp /></>
                                ) : (
                                    <>Show More <FaChevronDown /></>
                                )}
                            </button>
                        )}
                    </div>
                    </div>{/* end pages-two-columns */}
                </div>
            </div>

            {showDeleteModal && (
                <div className="form-modal-page-delete">
                    <div className="form-content-page-delete">
                        <h3>Are you sure you want to delete this page?</h3>
                        <div className="form-actions-page-delete">
                            <button onClick={deletePage} style={{ backgroundColor: "red" }}>Delete</button>
                            <button onClick={cancelDelete}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Pages;